import { useState, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Sidebar } from './Sidebar';
import SessionModal from '../components/SessionModal';
import { useAuth } from '../contexts/AuthContext';
import { useInactivityTimer } from '../hooks/useInactivityTimer';

const DRAWER_WIDTH = 210;

// Environment-based timeouts with defaults
const INACTIVITY_TIMEOUT_MS = (import.meta as any).env?.VITE_INACTIVITY_TIMEOUT_MINUTES
  ? parseInt((import.meta as any).env.VITE_INACTIVITY_TIMEOUT_MINUTES) * 60 * 1000
  : 15 * 60 * 1000; // 15 min default

const INACTIVITY_WARNING_MS = (import.meta as any).env?.VITE_INACTIVITY_WARNING_SECONDS
  ? parseInt((import.meta as any).env.VITE_INACTIVITY_WARNING_SECONDS) * 1000
  : 30 * 1000; // 30 seconds default warning

export function DashboardLayout() {
  const navigate = useNavigate();
  const { logout, refreshSession } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const timer = useInactivityTimer({
    timeout: INACTIVITY_TIMEOUT_MS,
    warningTime: INACTIVITY_WARNING_MS,
    onTimeout: () => {
      logout().then(() => navigate('/login', { state: { message: 'Sesión cerrada por inactividad' } }));
    },
    onExtend: () => {},
    enabled: true,
  });

  const handleExtend = useCallback(async () => {
    setIsExtending(true);
    try {
      await refreshSession();
      timer.extend();
    } catch {
      // If refresh fails, logout
      await logout();
      navigate('/login');
    } finally {
      setIsExtending(false);
    }
  }, [refreshSession, logout, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar (mobile) */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          display: { md: 'none' },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle sidebar"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src="https://w7.pngwing.com/pngs/964/371/png-transparent-cesar-vallejo-university-trujillo-trilce-club-deportivo-universidad-cesar-vallejo-logo-ucv-angle-text-rectangle.png"
              alt="UCV Logo"
              height={28}
              style={{ borderRadius: 3 }}
            />
            <Typography variant="h6" noWrap component="div" sx={{ fontSize: '1rem' }}>
              OCRI Callao
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Sidebar
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          drawerWidth={DRAWER_WIDTH}
        />
        {/* Desktop drawer */}
        <Sidebar
          variant="permanent"
          open={true}
          drawerWidth={DRAWER_WIDTH}
          sx={{ display: { xs: 'none', md: 'block' } }}
        />
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1.25,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          mt: { xs: 8, md: 0 },
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>

      {/* Session inactivity modal */}
      <SessionModal
        open={timer.isWarning}
        remaining={timer.remaining}
        onExtend={handleExtend}
        onLogout={async () => {
          await logout();
          navigate('/login');
        }}
        isExtending={isExtending}
      />
    </Box>
  );
}
